import React, { useState, useEffect } from 'react';
import smartScheduleService from '../services/smartScheduleService';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Domingo' },
  { value: 2, label: 'Segunda' },
  { value: 3, label: 'Terça' },
  { value: 4, label: 'Quarta' },
  { value: 5, label: 'Quinta' },
  { value: 6, label: 'Sexta' },
  { value: 7, label: 'Sábado' }
];

const SmartScheduleConfig = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [config, setConfig] = useState(null);

  const [formData, setFormData] = useState({
    config_name: 'Default',
    is_active: true,
    available_days: [6],
    time_windows: [
      { start: '09:00', end: '12:00' },
      { start: '14:00', end: '17:00' }
    ],
    default_duration_minutes: 60,
    max_appointments_per_day: 2,
    buffer_time_minutes: 15,
    search_ahead_days: 14,
    allow_weekends: false,
    allow_same_day: false
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await smartScheduleService.getConfig();
      if (response.success && response.data) {
        setConfig(response.data);
        setFormData({
          config_name: response.data.config_name || 'Default',
          is_active: response.data.is_active ?? true,
          available_days: response.data.available_days || [6],
          time_windows: response.data.time_windows || [
            { start: '09:00', end: '12:00' },
            { start: '14:00', end: '17:00' }
          ],
          default_duration_minutes: response.data.default_duration_minutes || 60,
          max_appointments_per_day: response.data.max_appointments_per_day || 2,
          buffer_time_minutes: response.data.buffer_time_minutes || 15,
          search_ahead_days: response.data.search_ahead_days || 14,
          allow_weekends: response.data.allow_weekends ?? false,
          allow_same_day: response.data.allow_same_day ?? false
        });
      }
    } catch (err) {
      setError('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await smartScheduleService.updateConfig(formData);
      if (response.success) {
        setSuccess('Configuração salva com sucesso!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Erro ao salvar');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day].sort();
      return { ...prev, available_days: days };
    });
  };

  const handleTimeWindowChange = (index, field, value) => {
    setFormData(prev => {
      const windows = [...prev.time_windows];
      windows[index] = { ...windows[index], [field]: value };
      return { ...prev, time_windows: windows };
    });
  };

  const addTimeWindow = () => {
    setFormData(prev => ({
      ...prev,
      time_windows: [...prev.time_windows, { start: '09:00', end: '12:00' }]
    }));
  };

  const removeTimeWindow = (index) => {
    if (formData.time_windows.length > 1) {
      setFormData(prev => ({
        ...prev,
        time_windows: prev.time_windows.filter((_, i) => i !== index)
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">⚙️ Configuração de Agenda</h1>
            <p className="text-gray-600">Configure os dias e horários disponíveis para Levantamentos</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Status */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Status</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="h-5 w-5 text-blue-600 rounded"
              />
              <span className="font-medium">Configuração Ativa</span>
            </label>
          </div>

          {/* Days of Week */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Dias Disponíveis</h2>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map(day => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => handleDayToggle(day.value)}
                  className={`px-4 py-2 rounded-lg border-2 transition ${
                    formData.available_days.includes(day.value)
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Selecione os dias da semana em que levantamentos podem ser agendados
            </p>
          </div>

          {/* Time Windows */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Horários Disponíveis</h2>
            {formData.time_windows.map((window, index) => (
              <div key={index} className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Das</label>
                  <input
                    type="time"
                    value={window.start}
                    onChange={(e) => handleTimeWindowChange(index, 'start', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">às</label>
                  <input
                    type="time"
                    value={window.end}
                    onChange={(e) => handleTimeWindowChange(index, 'end', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                {formData.time_windows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTimeWindow(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addTimeWindow}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              + Adicionar horário
            </button>
          </div>

          {/* Other Settings */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Outras Configurações</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duração padrão (minutos)
                </label>
                <input
                  type="number"
                  min="15"
                  max="480"
                  value={formData.default_duration_minutes}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    default_duration_minutes: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Máx. agendamentos por dia
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_appointments_per_day}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    max_appointments_per_day: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tempo buffer entre (minutos)
                </label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={formData.buffer_time_minutes}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    buffer_time_minutes: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dias à frente para buscar
                </label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={formData.search_ahead_days}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    search_ahead_days: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.allow_weekends}
                  onChange={(e) => setFormData(prev => ({ ...prev, allow_weekends: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">Permitir agendamento em finais de semana</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.allow_same_day}
                  onChange={(e) => setFormData(prev => ({ ...prev, allow_same_day: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">Permitir agendamento para o mesmo dia</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SmartScheduleConfig;
